import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Listing } from '@/types';
import { listingsAPI, handleApiError } from '@/services/api';
import toast from 'react-hot-toast';

interface ListingsState {
  listings: Listing[];
  myListings: Listing[];
  featuredListings: Listing[];
  currentListing: Listing | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  } | null;
  filters: {
    location: string;
    minPrice: number;
    maxPrice: number;
    amenities: string[];
    type: string;
  };
}

const initialState: ListingsState = {
  listings: [],
  myListings: [],
  featuredListings: [],
  currentListing: null,
  isLoading: false,
  error: null,
  pagination: null,
  filters: {
    location: '',
    minPrice: 0,
    maxPrice: 1000000,
    amenities: [],
    type: '',
  },
};

export const fetchListings = createAsyncThunk(
  'listings/fetchListings',
  async (params: {
    page?: number;
    limit?: number;
    location?: string;
    minPrice?: number;
    maxPrice?: number;
    amenities?: string[];
    type?: string;
  } = {}, { rejectWithValue }) => {
    try {
      // Use Supabase-based property service instead of mock API
      const { fetchProperties } = await import('@/services/propertyService');
      const properties = await fetchProperties(params);
      
      // Transform to match expected API response format
      return {
        data: properties,
        pagination: {
          current_page: 1,
          last_page: 1,
          per_page: properties.length,
          total: properties.length,
        }
      };
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchListingDetails = createAsyncThunk(
  'listings/fetchListingDetails',
  async (listingId: string, { rejectWithValue }) => {
    try {
      const { fetchPropertyById } = await import('@/services/propertyService');
      const property = await fetchPropertyById(listingId);
      if (!property) {
        throw new Error('Property not found');
      }
      return property;
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const createListing = createAsyncThunk(
  'listings/createListing',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await listingsAPI.create(data);
      toast.success('Property listed successfully!');
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateListing = createAsyncThunk(
  'listings/updateListing',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await listingsAPI.update(id, data);
      toast.success('Property updated successfully!');
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteListing = createAsyncThunk(
  'listings/deleteListing',
  async (id: string, { rejectWithValue }) => {
    try {
      await listingsAPI.delete(id);
      toast.success('Property deleted successfully!');
      return id;
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadListingImages = createAsyncThunk(
  'listings/uploadImages',
  async ({ listingId, images }: { listingId: string; images: File[] }, { rejectWithValue }) => {
    try {
      const response = await (listingsAPI as any).uploadImages(listingId, images);
      toast.success('Images uploaded successfully!');
      return { listingId, images: response.data };
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMyListings = createAsyncThunk(
  'listings/fetchMyListings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await listingsAPI.getMyListings();
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updatePropertyStatus = createAsyncThunk(
  'listings/updatePropertyStatus',
  async ({ id, status }: { id: string; status: string }, { getState, rejectWithValue }) => {
    // Simulate API update in mock data
    try {
      // Get current listings from state
      const state = getState() as any;
      const listings = state.listings.listings;
      const updatedListings = listings.map((listing: Listing) =>
        listing.id === id ? { ...listing, status } : listing
      );
      // Return updated listing
      const updated = updatedListings.find((l: Listing) => l.id === id);
      return updated;
    } catch (error) {
      return rejectWithValue('Failed to update property status');
    }
  }
);

const listingsSlice = createSlice({
  name: 'listings',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ListingsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setCurrentListing: (state, action: PayloadAction<Listing | null>) => {
      state.currentListing = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearListings: (state) => {
      state.listings = [];
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Listings
      .addCase(fetchListings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchListings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.listings = action.payload.data;
        state.pagination = {
          currentPage: action.payload.pagination.current_page,
          lastPage: action.payload.pagination.last_page,
          perPage: action.payload.pagination.per_page,
          total: action.payload.pagination.total,
        };
      })
      .addCase(fetchListings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Listing Details
      .addCase(fetchListingDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchListingDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentListing = action.payload;
      })
      .addCase(fetchListingDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Listing
      .addCase(createListing.fulfilled, (state, action) => {
        state.listings.unshift(action.payload);
      })
      // Update Listing
      .addCase(updateListing.fulfilled, (state, action) => {
        const index = state.listings.findIndex(listing => listing.id === action.payload.id);
        if (index !== -1) {
          state.listings[index] = action.payload;
        }
        if (state.currentListing?.id === action.payload.id) {
          state.currentListing = action.payload;
        }
      })
      // Delete Listing
      .addCase(deleteListing.fulfilled, (state, action) => {
        state.listings = state.listings.filter(listing => listing.id !== action.payload);
        if (state.currentListing?.id === action.payload) {
          state.currentListing = null;
        }
      })
      // Upload Images
      .addCase(uploadListingImages.fulfilled, (state, action) => {
        const { listingId, images } = action.payload;
        const listing = state.listings.find(l => l.id === listingId);
        if (listing) {
          listing.images = [...listing.images, ...images];
        }
        if (state.currentListing?.id === listingId) {
          state.currentListing.images = [...state.currentListing.images, ...images];
        }
      })
      // Fetch My Listings
      .addCase(fetchMyListings.fulfilled, (state, action) => {
        state.myListings = action.payload;
      })
      // Update Property Status
      .addCase(updatePropertyStatus.fulfilled, (state, action) => {
        if (action.payload) {
          const idx = state.listings.findIndex((l) => l.id === action.payload.id);
          if (idx !== -1) {
            state.listings[idx] = action.payload;
          }
          if (state.currentListing && state.currentListing.id === action.payload.id) {
            state.currentListing = action.payload;
          }
        }
      });
  },
});

export const { 
  setFilters, 
  clearFilters, 
  setCurrentListing, 
  clearError, 
  clearListings 
} = listingsSlice.actions;

export default listingsSlice.reducer; 